import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AlertTriangle,
  CheckCircle2,
  IndianRupee,
  Landmark,
  Megaphone,
  Percent,
  Receipt,
  Target,
  TrendingUp,
  CalendarClock,
} from 'lucide-react';
import type { ItcStatus } from '@mm/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { AppIcon } from '../components/AppIcon';
import { ForeignAmounts } from '../components/ForeignAmounts';
import { LiveRatesStrip } from '../components/LiveRates';
import { DateRangeFilter, useDateFilter } from '../components/DateRangeFilter';
import { itcBuckets } from '../utils/itc';
import { PerformanceCard } from '../features/reports/PerformanceCard';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/display';
import { useAuth } from '../features/auth/AuthProvider';
import { CreditBar } from '../features/billing/components';
import type {
  ForeignAmount,
  AccountRef,
  MarketingSummary,
  OtherSummary,
  PnlFigures,
  ProfitAndLoss,
} from '../types/models';
import { formatDate, formatDecimal, formatMoney, formatPercent, monthName } from '../utils/format';

interface Projected {
  month: string;
  daysInMonth: number;
  daysCounted: number;
  lastDate: string | null;
  revenue?: string;
  marketingExpense?: string;
  otherExpense?: string;
  dailyProfit?: string;
  projectedRevenue?: string;
  projectedProfit?: string;
}

interface DashboardData {
  startDate: string;
  endDate: string;
  marketing: MarketingSummary;
  /** Central P&L: estimated from daily entries, actual with month-end adjustments. */
  pnl: ProfitAndLoss;
  other: OtherSummary;
  /** Current month projection, whatever range is selected. */
  projected: Projected;
  marketingByPlatform: {
    platform?: { id: string; name: string };
    expenseBeforeGst: string;
    totalAmount: string;
  }[];
  marketingByApp: {
    app?: { id: string; name: string; iconUrl: string | null };
    expenseBeforeGst: string;
    gstAmount: string;
    totalAmount: string;
  }[];
  creditLines: {
    managerAccount: {
      id: string;
      name: string;
      accountIdentifier: string | null;
      gstAccount: { gstin: string };
    };
    creditLimit: string | null;
    used: string;
    available: string | null;
  }[];
  revenue: { count: number; revenueInr: string };
  revenueBySource: {
    source: { id: string; name: string; code: string };
    revenueInr: string;
    revenueForeign: ForeignAmount[];
  }[];
  revenueByApp: {
    app: { id: string; name: string; iconUrl: string | null };
    revenueInr: string;
    revenueForeign: ForeignAmount[];
  }[];
  missingYesterday: {
    date: string;
    marketing: MissingSection;
    revenue: MissingSection;
  };
}

interface MissingSection {
  status: string;
  expected: number;
  entered: number;
  missingAccounts: (AccountRef & { source?: { name: string } })[];
}

const needsAttention = (s?: MissingSection) =>
  !!s && s.status !== 'COMPLETE' && s.status !== 'NOT_EXPECTED';

function KpiCard({
  label,
  value,
  icon,
  tone = 'slate',
  emphasis,
  hint,
  foreign,
}: {
  label: string;
  value?: string;
  icon: ReactNode;
  tone?: 'slate' | 'profit' | 'loss' | 'gst';
  emphasis?: boolean;
  hint?: string;
  /** Revenue in its own currency, under the value. */
  foreign?: ForeignAmount[];
}) {
  return (
    <div
      className={clsx(
        // Container: the amount scales with the card's width so it never overflows.
        '@container min-w-0 rounded-xl border bg-white p-4 shadow-sm',
        emphasis ? 'border-emerald-200 ring-1 ring-emerald-100' : 'border-slate-200',
      )}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        <span
          className={clsx(
            'rounded-lg p-1.5',
            tone === 'profit' && 'bg-emerald-50 text-emerald-600',
            tone === 'loss' && 'bg-rose-50 text-rose-600',
            tone === 'gst' && 'bg-violet-50 text-violet-600',
            tone === 'slate' && 'bg-slate-100 text-slate-500',
          )}
        >
          {icon}
        </span>
      </div>
      <p
        className={clsx(
          'num mt-3 font-semibold whitespace-nowrap',
          emphasis ? 'text-[clamp(1rem,11.5cqi,1.875rem)]' : 'text-[clamp(0.95rem,11cqi,1.5rem)]',
          !value
            ? 'text-slate-300'
            : tone === 'gst'
              ? 'text-violet-700'
              : tone === 'loss'
                ? 'text-rose-700'
                : 'text-slate-900',
        )}
      >
        {value ?? '—'}
      </p>
      <ForeignAmounts items={foreign} className="mt-0.5" />
      <p className="mt-1 text-xs text-slate-400">{hint ?? ''}</p>
    </div>
  );
}

/**
 * This month's profit if the days still to come average like the days entered:
 * (revenue − marketing) per day × days in the month − other expenses entered.
 */
function ProjectedProfitCard({ projected: pr }: { projected?: Projected }) {
  const month = pr ? monthName(Number(pr.month.slice(5))) : '';
  const value = pr?.projectedProfit;
  return (
    <KpiCard
      label={`Projected Profit${month ? ` (${month})` : ''}`}
      value={pr ? (value ? formatMoney(value) : undefined) : undefined}
      icon={<CalendarClock className="size-4" />}
      tone={value && Number(value) < 0 ? 'loss' : 'profit'}
      hint={
        !pr
          ? undefined
          : !pr.lastDate
            ? 'No entries this month yet'
            : `${formatMoney(pr.dailyProfit)}/day × ${pr.daysInMonth} days − other ${formatMoney(pr.otherExpense)} · from ${pr.daysCounted} day${pr.daysCounted === 1 ? '' : 's'} entered`
      }
    />
  );
}

export function DashboardPage() {
  const { can } = useAuth();
  const [range, setRange] = useDateFilter('thisMonth');
  const query = useQuery({
    queryKey: ['dashboard', range.startDate, range.endDate],
    queryFn: () =>
      api.get<DashboardData>('/dashboard', { startDate: range.startDate, endDate: range.endDate }),
    placeholderData: keepPreviousData,
  });
  const d = query.data;
  const m = d?.marketing;
  const p = d?.pnl.actual;
  const adj = d?.pnl.adjustments;
  const missing = d?.missingYesterday;
  const maxApp = Math.max(1, ...(d?.marketingByApp ?? []).map((a) => Number(a.expenseBeforeGst)));

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={`${formatDate(range.startDate)} – ${formatDate(range.endDate)}`}
        actions={<DateRangeFilter value={range} onChange={setRange} />}
      />
      <LiveRatesStrip className="-mt-2 mb-4" />

      {missing && (needsAttention(missing.marketing) || needsAttention(missing.revenue)) && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="size-5 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-[1_1_16rem] space-y-1">
            <p className="font-medium">Data for {formatDate(missing.date)} is incomplete.</p>
            {(['marketing', 'revenue'] as const).map((k) => {
              const sec = missing[k];
              if (!needsAttention(sec)) return null;
              return (
                <p key={k} className="text-amber-800">
                  <span className="font-medium capitalize">{k}</span> {sec.entered}/{sec.expected}{' '}
                  accounts
                  {sec.missingAccounts.length > 0 &&
                    ` — missing: ${sec.missingAccounts
                      .map(
                        (a) =>
                          `${a.app?.name ?? ''} ${a.platform?.name ?? a.source?.name ?? ''} (${a.accountName})`,
                      )
                      .join(', ')}`}
                </p>
              );
            })}
          </div>
          {can('transactions.write') && (
            <Link to="/morning-entry">
              <Button size="sm">Open morning entry</Button>
            </Link>
          )}
        </div>
      )}
      {missing &&
        !needsAttention(missing.marketing) &&
        !needsAttention(missing.revenue) &&
        missing.marketing.expected + missing.revenue.expected > 0 && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <CheckCircle2 className="size-5 text-emerald-600" /> Yesterday's marketing and revenue
            data is complete.
          </div>
        )}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <KpiCard
          label="Marketing Spend"
          value={p && formatMoney(p.marketingExpense)}
          icon={<Megaphone className="size-4" />}
          hint={p ? `${formatMoney(p.marketingTotal)} incl. GST` : undefined}
        />
        <KpiCard
          label="Revenue"
          value={p && formatMoney(p.revenue)}
          foreign={p ? d?.pnl.revenueForeign : undefined}
          icon={<IndianRupee className="size-4" />}
          tone="profit"
          hint={
            adj && adj.months.length
              ? `Estimated ${formatMoney(d?.pnl.estimated.revenue)} before month-end actuals`
              : d
                ? d.revenueBySource
                    .map((s) => `${s.source.name} ${formatMoney(s.revenueInr)}`)
                    .slice(0, 2)
                    .join(' · ')
                : undefined
          }
        />
        <KpiCard
          label="Other Expenses"
          value={p && formatMoney(p.otherExpense)}
          icon={<Receipt className="size-4" />}
          hint={
            d && d.other.byCategory.length
              ? d.other.byCategory
                  .slice(0, 2)
                  .map((c) => `${c.category.name} ${formatMoney(c.amountBeforeGst)}`)
                  .join(' · ')
              : 'Salary, servers, software…'
          }
        />
        <KpiCard
          label="Operating Profit"
          value={p && formatMoney(p.operatingProfit)}
          icon={<TrendingUp className="size-4" />}
          tone={p && Number(p.operatingProfit) < 0 ? 'loss' : 'profit'}
          emphasis
          hint={
            adj && adj.months.length
              ? `After month-end actuals (${Number(adj.operatingProfit) >= 0 ? '+' : ''}${formatMoney(adj.operatingProfit)})`
              : 'Revenue − marketing − other expenses (GST excluded)'
          }
        />
        <KpiCard
          label="GST Input Credit"
          value={m && formatMoney(m.gstInputCredit)}
          icon={<Landmark className="size-4" />}
          tone="gst"
          hint={
            m
              ? `${formatMoney(itcBuckets(m.gstAmount, m.itcByStatus).remaining)} remaining to claim`
              : 'From marketing so far'
          }
        />
        <KpiCard
          label="ROAS"
          value={p && (p.roas == null ? '—' : `${formatDecimal(p.roas, 2)}×`)}
          icon={<Target className="size-4" />}
          hint="Revenue ÷ marketing spend"
        />
        <KpiCard
          label="ROI"
          value={p && formatPercent(p.roiPercent)}
          icon={<Percent className="size-4" />}
          hint="Operating profit ÷ marketing spend"
        />
        <ProjectedProfitCard projected={d?.projected} />
      </div>
      {adj && adj.partialMonths.length > 0 && (
        <p className="mt-2 text-xs text-slate-500">
          Month-end actuals for {adj.partialMonths.join(', ')} are not applied because the range
          covers only part of the month.
        </p>
      )}

      <div className="mt-6">
        <PerformanceCard
          startDate={range.startDate}
          endDate={range.endDate}
          title="Revenue vs expenses"
          showSummary={false}
        />
      </div>

      {d && (d.pnl.apps.length > 0 || d.pnl.companyLevel) && (
        <Card
          className="mt-6"
          title="Profit by app"
          description="Operating profit after month-end actuals; company-level expenses are shown separately"
          bodyClassName="p-0"
        >
          <div className="relative overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-5 py-2.5 text-left">App</th>
                  <th className="px-5 py-2.5 text-right">Marketing</th>
                  <th className="px-5 py-2.5 text-right">Revenue</th>
                  <th className="px-5 py-2.5 text-right">Other</th>
                  <th className="px-5 py-2.5 text-right">Profit</th>
                  <th className="px-5 py-2.5 text-right">ROAS</th>
                  <th className="px-5 py-2.5 text-right">ROI</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {d.pnl.apps.map((a) => (
                  <ProfitRow
                    key={a.app.id}
                    foreign={a.revenueForeign}
                    label={
                      <Link
                        to={`/apps/${a.app.id}`}
                        className="flex items-center gap-2 hover:text-brand-700"
                      >
                        <AppIcon src={a.app.iconUrl} name={a.app.name} size={24} />
                        {a.app.name}
                      </Link>
                    }
                    f={a.actual}
                  />
                ))}
                {d.pnl.companyLevel && (
                  <ProfitRow
                    label={<span className="text-slate-500">Company-level expenses</span>}
                    f={d.pnl.companyLevel}
                  />
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Marketing by app" description="Spend before GST" bodyClassName="p-0">
          {d && d.marketingByApp.length === 0 ? (
            <EmptyState title="No marketing spend in this period" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d?.marketingByApp.map((a) => (
                <li key={a.app?.id} className="flex items-center gap-3 px-5 py-3">
                  {a.app && <AppIcon src={a.app.iconUrl} name={a.app.name} size={28} />}
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between gap-2 text-sm">
                      <Link
                        to={`/apps/${a.app?.id}`}
                        className="truncate font-medium text-slate-900 hover:text-brand-700"
                      >
                        {a.app?.name}
                      </Link>
                      <span className="num font-medium">{formatMoney(a.expenseBeforeGst)}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{ width: `${(Number(a.expenseBeforeGst) / maxApp) * 100}%` }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Revenue by app" bodyClassName="p-0">
          {d && d.revenueByApp.length === 0 ? (
            <EmptyState title="No revenue in this period" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d?.revenueByApp.map((a) => (
                <li key={a.app.id} className="flex items-center gap-3 px-5 py-3 text-sm">
                  <AppIcon src={a.app.iconUrl} name={a.app.name} size={28} />
                  <Link
                    to={`/apps/${a.app.id}`}
                    className="flex-1 truncate font-medium text-slate-900 hover:text-brand-700"
                  >
                    {a.app.name}
                  </Link>
                  <span className="num text-right font-medium">
                    {formatMoney(a.revenueInr)}
                    <ForeignAmounts items={a.revenueForeign} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Marketing by platform" bodyClassName="p-0">
          <table className="min-w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {d?.marketingByPlatform.map((p) => (
                <tr key={p.platform?.id}>
                  <td className="px-5 py-3 text-slate-900">{p.platform?.name}</td>
                  <td className="num px-5 py-3 text-right">{formatMoney(p.expenseBeforeGst)}</td>
                  <td className="num px-5 py-3 text-right text-slate-500">
                    {formatMoney(p.totalAmount)} incl. GST
                  </td>
                </tr>
              ))}
              {d && d.marketingByPlatform.length === 0 && (
                <tr>
                  <td className="px-5 py-4 text-slate-500">No spend in this period.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>

        <Card
          title="Revenue by source"
          description="INR at the rate saved with each entry"
          actions={
            <Link to="/reports/revenue" className="text-sm text-brand-700 hover:underline">
              Report
            </Link>
          }
        >
          {d && d.revenueBySource.length === 0 ? (
            <p className="text-sm text-slate-500">No revenue in this period.</p>
          ) : (
            <dl className="space-y-3 text-sm">
              {d?.revenueBySource.map((s) => (
                <div key={s.source.id}>
                  <div className="flex justify-between">
                    <dt className="text-slate-700">{s.source.name}</dt>
                    <dd className="num text-right font-medium text-slate-900">
                      {formatMoney(s.revenueInr)}
                      <ForeignAmounts items={s.revenueForeign} />
                    </dd>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{
                        width: `${(Number(s.revenueInr) / Math.max(1, Number(d.revenue.revenueInr))) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </dl>
          )}
        </Card>

        <Card
          title="Credit lines (MCC)"
          description="Used vs limit today"
          actions={
            <Link to="/mcc-accounts" className="text-sm text-brand-700 hover:underline">
              Details
            </Link>
          }
        >
          {d && d.creditLines.length === 0 ? (
            <p className="text-sm text-slate-500">No manager accounts with credit lines yet.</p>
          ) : (
            <ul className="space-y-4">
              {d?.creditLines.map((c) => (
                <li key={c.managerAccount.id}>
                  <div className="mb-1 flex justify-between gap-2 text-sm">
                    <Link
                      to={`/mcc-accounts/${c.managerAccount.id}`}
                      className="font-medium text-slate-900 hover:text-brand-700"
                    >
                      {c.managerAccount.name}
                    </Link>
                    <span className="num text-emerald-700">
                      {formatMoney(c.available)} available
                    </span>
                  </div>
                  <CreditBar used={c.used} limit={c.creditLimit} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="GST from marketing"
          description="Tracked separately — never part of marketing expense"
        >
          {m ? <GstCard total={m.gstAmount} byStatus={m.itcByStatus} /> : null}
        </Card>
      </div>
    </>
  );
}

function GstCard({
  total,
  byStatus,
}: {
  total: string;
  byStatus: Partial<Record<ItcStatus, string>>;
}) {
  const b = itcBuckets(total, byStatus);
  const rows = [
    { label: 'Total GST', value: b.total, hint: 'GST paid on marketing' },
    { label: 'Eligible', value: b.eligible, hint: 'Input credit you can claim' },
    { label: 'Claimed', value: b.claimed, hint: 'Already claimed in returns' },
    { label: 'Remaining', value: b.remaining, hint: 'Eligible, not claimed yet' },
  ];
  return (
    <dl className="grid grid-cols-2 gap-3 text-sm">
      {rows.map((r) => (
        <div key={r.label} className="rounded-lg bg-violet-50/60 px-3 py-2">
          <dt className="text-xs text-slate-500">{r.label}</dt>
          <dd className="num text-base font-semibold text-violet-700">{formatMoney(r.value)}</dd>
          <dd className="text-[11px] text-slate-400">{r.hint}</dd>
        </div>
      ))}
    </dl>
  );
}

function ProfitRow({
  label,
  f,
  foreign,
}: {
  label: ReactNode;
  f: PnlFigures;
  foreign?: ForeignAmount[];
}) {
  const loss = Number(f.operatingProfit) < 0;
  return (
    <tr>
      <td className="px-5 py-2.5">{label}</td>
      <td className="num px-5 py-2.5 text-right">{formatMoney(f.marketingExpense)}</td>
      <td className="num px-5 py-2.5 text-right">
        {formatMoney(f.revenue)}
        <ForeignAmounts items={foreign} />
      </td>
      <td className="num px-5 py-2.5 text-right">{formatMoney(f.otherExpense)}</td>
      <td
        className={
          'num px-5 py-2.5 text-right font-semibold ' +
          (loss ? 'text-rose-700' : 'text-emerald-700')
        }
      >
        {formatMoney(f.operatingProfit)}
      </td>
      <td className="num px-5 py-2.5 text-right">
        {f.roas == null ? '—' : `${formatDecimal(f.roas, 2)}×`}
      </td>
      <td className="num px-5 py-2.5 text-right">{formatPercent(f.roiPercent)}</td>
    </tr>
  );
}
